import { useContext, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { GameContext } from '../../../contexts/GameContext';
import TurnActionButton from './TurnActionButton';
import TurnStateText from './TurnStateText';
import { TurnStateEnum } from '../../../Enums/TurnStateEnum';
import { useTranslation } from '../../../contexts/LanguageContext';
import SocketContext from '../../../contexts/SocketContext';
import ReadyButton from './ReadyButton';

const yourTurnKeyframes = {
    '@keyframes yourTurnGlow': {
        '0%, 100%': {
            boxShadow: '0 0 20px rgba(193, 145, 255, 0.15), 0 0 40px rgba(193, 145, 255, 0.1)',
        },
        '50%': {
            boxShadow: '0 0 40px rgba(193, 145, 255, 0.45), 0 0 80px rgba(193, 145, 255, 0.25)',
        },
    },
    '@keyframes bounceIn': {
        '0%': { transform: 'scale(0.7)', opacity: 0 },
        '60%': { transform: 'scale(1.08)', opacity: 1 },
        '100%': { transform: 'scale(1)' },
    },
    '@keyframes fadeSlideDown': {
        '0%': { transform: 'translateY(-12px)', opacity: 0 },
        '100%': { transform: 'translateY(0)', opacity: 1 },
    },
};

const myTurnGlowSx = {
    ...yourTurnKeyframes,
    animation: 'bounceIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), yourTurnGlow 2s ease-in-out infinite 0.5s',
    borderRadius: '16px',
    padding: '16px 24px',
};

const ActionBar = () => {
    const gameContext = useContext(GameContext);
    const { socket } = useContext(SocketContext).SocketState;
    const { t } = useTranslation();
    const [turnStateText, setTurnStateText] = useState('');
    const [buttonTurnStateText1, setButtonTurnStateText1] = useState('');
    const [buttonTurnStateText2, setButtonTurnStateText2] = useState('');

    const playersReady = gameContext.currentTurn?.playersReady ?? [];
    const nonTurnHolders = gameContext.players.filter(
        (p) => p.playerId !== gameContext.currentTurn?.turnHolder.playerId
    );
    const notReadyPlayers = nonTurnHolders.filter((p) => !playersReady.includes(p.playerId));
    const nonTurnHolderCount = nonTurnHolders.length;
    const allReady = nonTurnHolderCount === 0 || playersReady.length >= nonTurnHolderCount;
    const isPlayerReady = playersReady.includes(socket?.id ?? '');

    const animationKey = `${gameContext.currentTurn?.turnState}-${gameContext.currentTurn?.turnCount}`;

    useEffect(() => {
        if (gameContext === undefined || gameContext.currentTurn === undefined) {
            return;
        }

        switch (gameContext.currentTurn.turnState) {
            case undefined:
                setTurnStateText('current turn undefined');
                break;

            case TurnStateEnum.waitingTurnHolderValidation:
                setButtonTurnStateText1(`${t('confirmChoice')}`);
                setButtonTurnStateText2('');
                setTurnStateText(`${gameContext.currentTurn?.turnHolder.playerName} ${t('thinking')}`);
                break;

            case TurnStateEnum.waitingEndOfTurnValidation:
                if (gameContext.isPlayerTurn()) {
                    setButtonTurnStateText1(t('waitingBets'));
                    setButtonTurnStateText2(t('endTurn'));
                    return;
                }
                setTurnStateText(t('yourBet'));

                break;

            case TurnStateEnum.turnFinished:
                const winnerId = gameContext.currentTurn.winnerId;
                let loseMessage = undefined;

                if (winnerId) {
                    loseMessage = t('winnerIs', { name: gameContext.getWinnerName(winnerId) });
                } else {
                    loseMessage = t('nobodyWon');
                }
                setButtonTurnStateText1(gameContext.hasPlayerWin() ? t('won') : loseMessage);
                setButtonTurnStateText2(t('newTurn'));
                setTurnStateText(gameContext.hasPlayerWin() ? t('won') : loseMessage);
        }
    }, [gameContext, gameContext.currentTurn, playersReady.length, t]);

    if (gameContext.currentTurn === undefined) {
        return <></>;
    }

    if (gameContext.isPlayerTurn()) {
        const showReadyCount = gameContext.currentTurn.turnState === TurnStateEnum.waitingEndOfTurnValidation && nonTurnHolderCount > 0 && notReadyPlayers.length > 0;
        const isMyAction = gameContext.currentTurn.turnState === TurnStateEnum.waitingTurnHolderValidation;
        return (
            <Box
                key={animationKey}
                sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    ...(isMyAction ? myTurnGlowSx : {}),
                }}
            >
                {isMyAction && (
                    <Typography
                        variant="h6"
                        sx={{
                            ...yourTurnKeyframes,
                            color: 'white',
                            mb: 1.5,
                            animation: 'fadeSlideDown 0.6s ease-out 0.2s both',
                            letterSpacing: '0.5px',
                        }}
                        textTransform="none"
                    >
                        {t('yourTurnPrefix')}
                        <span style={{ color: gameContext.currentTurn.turnHolder.playerColor || 'white' }}>
                            {gameContext.currentTurn.turnHolder.playerName}
                        </span>
                        {' !'}
                    </Typography>
                )}
                {showReadyCount && (
                    <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 1 }} textTransform="none">
                        {t('waitingForPlayers', {
                            names: notReadyPlayers.map((p) => p.playerName).join(', '),
                        })}
                    </Typography>
                )}
                <TurnActionButton text1={buttonTurnStateText1} text2={buttonTurnStateText2} allPlayersReady={allReady} />
            </Box>
        );
    }

    // Non-turn-holder during betting phase: show ready button
    if (gameContext.currentTurn.turnState === TurnStateEnum.waitingEndOfTurnValidation) {
        return (
            <Box
                key={animationKey}
                sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    ...myTurnGlowSx,
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        ...yourTurnKeyframes,
                        color: 'white',
                        mb: 1.5,
                        animation: 'fadeSlideDown 0.6s ease-out 0.2s both',
                        letterSpacing: '0.5px',
                    }}
                    textTransform="none"
                >
                    {t('yourBet')}
                </Typography>
                <ReadyButton isReady={isPlayerReady} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                position: 'relative',
            }}
        >
            <TurnStateText text={turnStateText} />
        </Box>
    );
};

export default ActionBar;
